import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_TRIES = 30;      // ~ 30 s at 1 s intervals
const POLL_INTERVAL_MS = 1000;

/**
 * Stripe redirects here with `?session_id=cs_test_...`. We poll
 * `/api/payments/status/{session_id}` (which falls back to querying
 * Stripe directly when webhooks are late) until `payment_status = paid`,
 * then refresh AuthContext and bounce to /account.
 */
const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | paid | pending | failed | error
  const [tries, setTries] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sessionId = params.get('session_id');
    if (!sessionId) { setStatus('error'); return; }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setTries(attempts);
      try {
        const res = await fetch(`${API}/api/payments/status/${encodeURIComponent(sessionId)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || 'Status check failed.');
        if (data.payment_status === 'paid') {
          setStatus('paid');
          // Guest checkout: sign them in off the confirmed payment.
          try {
            await fetch(`${API}/api/payments/claim/${sessionId}`, {
              method: 'POST',
              credentials: 'include',
            });
          } catch { /* refresh below will still pick up an existing session */ }
          await refresh();
          const plan = data.plan;
          // Plausible revenue goal - fired once per session id.
          try {
            const key = `om_purchase_tracked_${sessionId}`;
            if (window.plausible && !sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              const cents = data.amount_total ?? data.amount_cents ?? 0;
              window.plausible('Purchase', {
                props: { plan: plan || 'unknown' },
                revenue: { currency: 'AUD', amount: Number((cents / 100).toFixed(2)) },
              });
            }
          } catch { /* analytics never blocks the redirect */ }
          setTimeout(
            () => navigate(plan === 'founding' ? '/founders?joined=1' : '/account?welcome=1', { replace: true }),
            300,
          );
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed');
          return;
        }
        if (attempts >= MAX_TRIES) {
          setStatus('pending');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_TRIES) { setStatus('error'); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="payment-success-page">
      <TopNav />
      <main className="max-w-lg mx-auto px-4 py-16 text-center">
        {status === 'paid' ? (
          <>
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" data-testid="payment-success-icon" />
            <p className="text-emerald-400 text-[10px] font-black uppercase tracking-[0.3em] mb-2">Payment confirmed</p>
            <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              You&apos;re in.
            </h1>
            <p className="text-zinc-400 text-sm">Redirecting you to your account…</p>
          </>
        ) : status === 'pending' ? (
          <>
            <p className="text-yellow-400 text-[10px] font-black uppercase tracking-[0.3em] mb-2">Waiting on Stripe</p>
            <h1 className="text-2xl font-black mb-2" style={{ fontFamily: 'Georgia, serif' }}>Almost there.</h1>
            <p className="text-zinc-400 text-sm mb-6">
              Your payment is confirmed at Stripe but our system hasn&apos;t caught up yet. Refresh in a minute or head to your account - the plan will appear as soon as we get the callback.
            </p>
            <Link to="/account" className="text-red-400 underline text-sm">Go to Account →</Link>
          </>
        ) : status === 'failed' ? (
          <>
            <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-2">Payment didn&apos;t go through</p>
            <h1 className="text-2xl font-black mb-2" style={{ fontFamily: 'Georgia, serif' }}>Try again?</h1>
            <p className="text-zinc-400 text-sm mb-6">No charge was made. You can pick a plan again from the subscribe page.</p>
            <Link to="/subscribe" className="text-red-400 underline text-sm">Back to plans →</Link>
          </>
        ) : status === 'error' ? (
          <>
            <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-2">Something went wrong</p>
            <p className="text-zinc-400 text-sm mb-6">We couldn&apos;t look up that payment. If you were charged, email ryan@outlawmedia.com.au and we&apos;ll sort it.</p>
            <Link to="/account" className="text-red-400 underline text-sm">Go to Account →</Link>
          </>
        ) : (
          <>
            <div className="inline-block h-8 w-8 rounded-full border-2 border-red-500 border-t-transparent animate-spin mb-4" />
            <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-1">Confirming payment</p>
            <p className="text-zinc-400 text-sm">Hold tight… ({tries}/{MAX_TRIES})</p>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default PaymentSuccess;
